import React from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateWithFormatter } from '@/utils/function'
import type { ContactSource } from '@/modules/Marketing/types/response'

interface Props {
    contactSource: ContactSource
}

/**
 * Read-only panel surfacing lead-origin data that the editable form does not
 * show: company size / specific needs / deployment time, plus the campaign
 * attribution captured at submit time (`metadata`: utm tracking, landing URL,
 * referer, host, user agent, submitted-at). These are facts about where the
 * lead came from — sales views them, does not edit them.
 */

/** A `tracking` value is either a utm query string
 *  (utm_source=..&utm_medium=..) or a plain referrer URL — render accordingly. */
function parseTracking(raw?: string): { utm: string; raw: string } | null {
    if (!raw) return null
    const looksLikeQuery =
        raw.includes('utm_') ||
        (raw.includes('=') && !/^https?:\/\//i.test(raw))
    if (looksLikeQuery) {
        const p = new URLSearchParams(raw)
        const utm = [
            p.get('utm_source') && `source=${p.get('utm_source')}`,
            p.get('utm_medium') && `medium=${p.get('utm_medium')}`,
            p.get('utm_campaign') && `campaign=${p.get('utm_campaign')}`,
        ]
            .filter(Boolean)
            .join(' · ')
        if (utm) return { utm, raw }
    }
    return { utm: '', raw }
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <div className="flex gap-2 text-xs py-1">
        <span className="text-gray-500 shrink-0 w-32">{label}</span>
        <span className="text-gray-800 break-all min-w-0">{children}</span>
    </div>
)

const LeadSourceTrackingPanel: React.FC<Props> = ({ contactSource }) => {
    // Global namespace for pre-existing field labels; marketing namespace for the
    // new tracking keys (global i18n files are off-limits — module locales only).
    const { t } = useTranslation()
    const { t: tMar } = useTranslation('marketing')
    const meta = contactSource.metadata || {}
    const companySize = contactSource.company_size
    const specificNeeds = contactSource.specific_needs
    const deploymentTime = contactSource.deployment_time
    const tracking = parseTracking(meta.tracking)

    const hasAny =
        companySize ||
        specificNeeds ||
        deploymentTime ||
        meta.tracking ||
        meta.currentUrl ||
        meta.referer ||
        meta.host ||
        meta.userAgent ||
        meta.submittedAt ||
        meta.country

    if (!hasAny) return null

    return (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-xs font-semibold text-gray-700 mb-2">
                {tMar('lead-source-tracking')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {companySize && (
                    <Row label={t('company-size')}>{companySize}</Row>
                )}
                {specificNeeds && (
                    <Row label={t('specific-needs')}>{specificNeeds}</Row>
                )}
                {deploymentTime && (
                    <Row label={t('deployment-time')}>{deploymentTime}</Row>
                )}
                {tracking && (
                    <Row label={tMar('tracking-utm')}>
                        {tracking.utm || tracking.raw}
                    </Row>
                )}
                {meta.currentUrl && (
                    <Row label={tMar('tracking-landing-url')}>
                        <a
                            href={meta.currentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            {meta.currentUrl}
                        </a>
                    </Row>
                )}
                {meta.referer && (
                    <Row label={tMar('tracking-referer')}>{meta.referer}</Row>
                )}
                {meta.host && (
                    <Row label={tMar('tracking-host')}>{meta.host}</Row>
                )}
                {meta.country && (
                    <Row label={tMar('tracking-country')}>{meta.country}</Row>
                )}
                {meta.submittedAt && (
                    <Row label={tMar('tracking-submitted-at')}>
                        {formatDateWithFormatter(
                            meta.submittedAt,
                            'DD/MM/YYYY HH:mm'
                        )}
                    </Row>
                )}
                {meta.userAgent && (
                    <Row label={tMar('tracking-user-agent')}>
                        {meta.userAgent}
                    </Row>
                )}
            </div>
        </div>
    )
}

export default LeadSourceTrackingPanel
